import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { FiEye, FiEyeOff, FiLock, FiShield } from "react-icons/fi";
import { CHANGE_PASSWORD } from "../../constant";
import { PUT } from "../../constant/apiMethods";
import Labels from "../../services/apiLabels";
import { getAuthUserId } from "../../services/localStorage";

const MIN_LEN = 8;

function PasswordField({
  id,
  name,
  label,
  placeholder,
  value,
  onChange,
  autoComplete,
  visible,
  onToggleVisible,
  error,
  hint,
}) {
  return (
    <div className={`dd-field${error ? " is-invalid" : ""}`}>
      <label htmlFor={id}>{label}</label>
      <div className="dd-password__input">
        <input
          type={visible ? "text" : "password"}
          className="form-control"
          name={name}
          id={id}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required
          autoComplete={autoComplete}
        />
        <button
          type="button"
          className="dd-password__toggle"
          onClick={onToggleVisible}
          aria-label={visible ? "Hide password" : "Show password"}
        >
          {visible ? <FiEyeOff size={16} /> : <FiEye size={16} />}
        </button>
      </div>
      {error ? <p className="dd-password__error">{error}</p> : null}
      {!error && hint ? <p className="dd-password__hint">{hint}</p> : null}
    </div>
  );
}

function ChangePassword({ ChangePassword: submitChangePassword, loading, success }) {
  const [formData, setFormData] = useState({
    password: "",
    newpassword: "",
    confirmpassword: "",
  });
  const [visible, setVisible] = useState({
    password: false,
    newpassword: false,
    confirmpassword: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState("");
  const pendingSave = useRef(false);
  const id = getAuthUserId();

  const checks = useMemo(() => {
    const next = formData.newpassword || "";
    const confirm = formData.confirmpassword || "";
    return {
      length: next.length >= MIN_LEN,
      different: next.length > 0 && next !== formData.password,
      match: confirm.length > 0 && next === confirm,
    };
  }, [formData]);

  const confirmError =
    submitted && formData.confirmpassword && !checks.match
      ? "New password and confirmation do not match."
      : "";

  const lengthError =
    submitted && formData.newpassword && !checks.length
      ? `Use at least ${MIN_LEN} characters.`
      : submitted && formData.newpassword && !checks.different
      ? "New password must be different from the current password."
      : "";

  const isValid =
    Boolean(formData.password) &&
    checks.length &&
    checks.different &&
    checks.match;

  const onChange = (e) => {
    setResult("");
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const toggleVisible = (key) => {
    setVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    const { password, newpassword, confirmpassword } = formData;
    if (newpassword.length < MIN_LEN) return;
    if (newpassword === password) return;
    if (newpassword !== confirmpassword) return;
    pendingSave.current = true;
    submitChangePassword({
      old_password: password,
      new_password: newpassword,
      userId: id,
    });
  };

  useEffect(() => {
    if (!pendingSave.current || loading) return;
    pendingSave.current = false;
    if (success) {
      setFormData({ password: "", newpassword: "", confirmpassword: "" });
      setSubmitted(false);
      setResult("Password updated.");
    } else {
      setResult("Unable to update password. Check your current password and try again.");
    }
  }, [loading, success]);

  return (
    <div className="dd-section">
      <div className="dd-password">
        <div className="dd-password__intro">
          <span className="dd-password__badge" aria-hidden>
            <FiShield size={20} />
          </span>
          <div>
            <h2>Secure your dealer desk</h2>
            <p>
              Choose a strong password you do not reuse elsewhere. You will stay
              signed in after updating.
            </p>
          </div>
        </div>

        <form className="dd-form dd-password__form" onSubmit={handleSubmit}>
          <PasswordField
            id="password"
            name="password"
            label="Current password"
            placeholder="Enter current password"
            value={formData.password}
            onChange={onChange}
            autoComplete="current-password"
            visible={visible.password}
            onToggleVisible={() => toggleVisible("password")}
          />

          <div className="dd-password__divider" aria-hidden>
            <FiLock size={14} />
            <span>New credentials</span>
          </div>

          <PasswordField
            id="newpassword"
            name="newpassword"
            label="New password"
            placeholder="Create a new password"
            value={formData.newpassword}
            onChange={onChange}
            autoComplete="new-password"
            visible={visible.newpassword}
            onToggleVisible={() => toggleVisible("newpassword")}
            error={lengthError}
          />

          <PasswordField
            id="confirmpassword"
            name="confirmpassword"
            label="Confirm new password"
            placeholder="Re-enter new password"
            value={formData.confirmpassword}
            onChange={onChange}
            autoComplete="new-password"
            visible={visible.confirmpassword}
            onToggleVisible={() => toggleVisible("confirmpassword")}
            error={confirmError}
            hint={
              checks.match
                ? "Passwords match."
                : formData.confirmpassword
                ? "Waiting for a match…"
                : ""
            }
          />

          <ul className="dd-password__checks" aria-label="Password requirements">
            <li className={checks.length ? "is-ok" : ""}>
              At least {MIN_LEN} characters
            </li>
            <li className={checks.different ? "is-ok" : ""}>
              Different from current password
            </li>
            <li className={checks.match ? "is-ok" : ""}>
              Confirmation matches
            </li>
          </ul>

          <div className="dd-btn-row">
            {result ? (
              <p
                className={
                  result.startsWith("Password updated")
                    ? "dd-password__hint"
                    : "dd-password__error"
                }
                role="status"
              >
                {result}
              </p>
            ) : null}
            <button
              className="dd-btn dd-btn--primary btn_contacta"
              type="submit"
              disabled={!isValid || loading}
            >
              {loading ? "Updating…" : "Update password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  loading: Boolean(state.profile.passwordLoading),
  success: Boolean(state.profile.passwordSuccess),
});

const mapDispatchToProps = (dispatch) => ({
  ChangePassword: (postData) => {
    const { userId, ...data } = postData || {};
    return dispatch({
      type: CHANGE_PASSWORD,
      payload: {
        type: CHANGE_PASSWORD,
        label: `${Labels.changePassword}${userId || ""}/`,
        method: PUT,
        hideOverlayLoader: true,
        data,
      },
    });
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ChangePassword);
